import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import { useListings } from '../hooks/useListings';
import { pickLang } from '../utils/listingDisplay';
import ContactForm from '../components/contact/ContactForm';

export const CONTACT_EMAIL = 'info@neve-shamir-community.org';

/**
 * Contact us (plan 16). ?topic= and ?listing= pre-fill the form (the "Ask about
 * this listing" link on listing pages).
 */
export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  usePageTitle(t('seo.contact'), { description: t('seo.desc.contact') });
  const [searchParams] = useSearchParams();
  const { listings } = useListings();

  const options = useMemo(
    () =>
      listings
        .map((l) => ({ id: l.id, label: pickLang(l.nameEn, l.nameHe, isHebrew) }))
        .sort((a, b) => a.label.localeCompare(b.label, isHebrew ? 'he' : 'en')),
    [listings, isHebrew]
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-lg font-semibold text-ink">{t('contact.title')}</h1>
        <p className="text-sm text-ash">{t('contact.intro')}</p>
      </div>
      <ContactForm
        // Remount when the link changes the pre-filled listing.
        key={`${searchParams.get('topic') ?? ''}-${searchParams.get('listing') ?? ''}`}
        source="public"
        listings={options}
        initialTopic={searchParams.get('topic') ?? undefined}
        initialListingId={searchParams.get('listing') ?? undefined}
      />
      <p className="flex items-center gap-2 text-sm text-ash">
        <Mail className="w-4 h-4 shrink-0" aria-hidden />
        <span>
          {t('contact.orEmail')}{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-ink underline" dir="ltr">
            {CONTACT_EMAIL}
          </a>
        </span>
      </p>
    </div>
  );
}
